/**
 * Mutation hooks for Clubs
 * 
 * These hooks handle club mutations (join, leave, approve, etc.)
 * with automatic cache invalidation.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query/client';
import { BACKEND_URL } from "../../config";
/**
 * Request to join a club
 */
export function useJoinClubRequest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      const res = await api.post(`/api/clubs/${clubId}/join`, {});
      return res.data;
    },
    onSuccess: (_, clubId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId) });
    },
  });
}

/**
 * Leave a club
 */
export function useLeaveClub() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      await api.delete(`/api/clubs/${clubId}/leave`);
      return clubId;
    },
    onSuccess: (clubId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.members(clubId) });
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
    },
  });
}

/**
 * Accept a club invitation
 */
export function useAcceptClubInvite() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      await api.post(`/api/clubs/${clubId}/accept-invite`, {});
      return clubId;
    },
    onSuccess: (clubId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.members(clubId) });
    },
  });
}

/**
 * Decline a club invitation
 */
export function useDeclineClubInvite() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      await api.post(`/api/clubs/${clubId}/decline-invite`, {});
      return clubId;
    },
    onSuccess: (clubId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId) });
    },
  });
}

/**
 * Approve a member request (moderator only)
 */
export function useApproveMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clubId, userId }) => {
      await api.post(`/api/clubs/${clubId}/approve/${userId}`, {});
      return { clubId, userId };
    },
    onSuccess: ({ clubId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.members(clubId) });
    },
  });
}

/**
 * Reject a member request (moderator only)
 */
export function useRejectMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clubId, userId, reason }) => {
      await api.post(`/api/clubs/${clubId}/reject/${userId}`, { reason });
      return { clubId, userId };
    },
    onSuccess: ({ clubId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.members(clubId) });
    },
  });
}

/**
 * Remove a member (moderator only)
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clubId, userId }) => {
      await api.delete(`/api/clubs/${clubId}/members/${userId}`);
      return { clubId, userId };
    },
    onSuccess: ({ clubId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.members(clubId) });
    },
  });
}

/**
 * Promote member to moderator
 */
export function usePromoteToModerator() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clubId, userId }) => {
      await api.post(`/api/clubs/${clubId}/promote/${userId}`, {});
      return { clubId, userId };
    },
    onSuccess: ({ clubId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.members(clubId) });
    },
  });
}

/**
 * Demote moderator to member
 */
export function useDemoteFromModerator() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clubId, userId }) => {
      await api.post(`/api/clubs/${clubId}/demote/${userId}`, {});
      return { clubId, userId };
    },
    onSuccess: ({ clubId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.members(clubId) });
    },
  });
}


/**
 * Cancel a join request to a club
 */
export function useCancelJoinRequest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      await api.delete(`/api/clubs/${clubId}/cancel-request`);
      return { clubId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
    },
  });
}

/**
 * Accept an invitation to join a club
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      const res = await api.post(`/api/clubs/${clubId}/invitation/accept`, {});
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
    },
  });
}

/**
 * Decline an invitation to join a club
 */
export function useDeclineInvitation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      await api.post(`/api/clubs/${clubId}/invitation/decline`, {});
      return { clubId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
    },
  });
}

/**
 * Step down from moderator role in a club
 */
export function useStepDownFromModerator() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      await api.post(`/api/clubs/${clubId}/step-down`, {});
      return { clubId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
    },
  });
}

/**
 * Delete a club (owners/moderators only)
 */
export function useDeleteClub() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubId) => {
      await api.delete(`/api/clubs/${clubId}`);
      return { clubId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all() });
    },
  });
}

/**
 * Create a new club
 */
export function useCreateClub() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clubData) => {
      const res = await api.post("/api/clubs", clubData);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all() });
    },
  });
}

